import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIncidents } from '../hooks/api';
import { RiskBadge } from './common/Badges';
import { relative } from '../utils/format';
import { EmptyState } from './common/EmptyState';
import { useDismissedAlerts } from './useDismissedAlerts';

/**
 * Attention centre: the highest-risk open incidents.
 *
 * Not realtime — it reflects the last fetch and links into the queue.
 *
 * **What is listed here are open critical incidents, not messages**, which
 * governs what "clear" is allowed to mean. Dismissing marks one as seen in this
 * panel; it does not acknowledge, resolve or hide the incident anywhere else,
 * and an incident that gets a new event returns to the list because the
 * dismissal is keyed to its last event rather than to its id.
 *
 * The alternative — a clear that made a critical incident go away — would let a
 * bad afternoon be tidied out of view, which is the one outcome an attention
 * centre must not permit.
 */

/**
 * Fetched deeper than the five displayed so the badge can be counted honestly.
 *
 * The badge must say how many need attention, and that cannot be derived from
 * a page of five once some are dismissed. Beyond this depth the count falls
 * back to the server's total, which is correct because an incident never shown
 * cannot have been dismissed.
 */
const FETCH_LIMIT = 20;
const SHOW_LIMIT = 5;

export function NotificationCentre() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const navigate = useNavigate();
  const dismissed = useDismissedAlerts();

  const { data } = useIncidents({
    status: 'OPEN',
    riskLevel: 'CRITICAL',
    pageSize: FETCH_LIMIT,
    sort: 'currentRiskScore',
  });

  const fetched = data?.items ?? [];
  const total = data?.totalItems ?? 0;
  const live = fetched.filter((i) => !dismissed.isDismissed(i.id, i.lastEventAt));
  const hiddenHere = fetched.length - live.length;

  /*
   * Anything beyond the fetched page has never been displayed, so it cannot
   * have been dismissed and must still be counted. Without this the badge would
   * silently under-report a fleet with more than twenty critical incidents.
   */
  const unseenBeyondPage = Math.max(0, total - fetched.length);
  const badgeCount = live.length + unseenBeyondPage;

  const visible = (showDismissed ? fetched : live).slice(0, SHOW_LIMIT);

  return (
    <>
      <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="Attention centre">
        <Badge color="error" badgeContent={badgeCount} max={99}>
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380 } }}
      >
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" sx={{ flex: 1 }}>
            Needs attention
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Critical · open
          </Typography>
        </Box>
        <Divider />

        {visible.length === 0 ? (
          <EmptyState
            title={live.length === 0 && hiddenHere > 0 ? 'Nothing new' : 'All clear'}
            message={
              live.length === 0 && hiddenHere > 0
                ? 'Everything critical here has been marked as seen. The incidents are still open.'
                : 'No critical open incidents right now.'
            }
          />
        ) : (
          <List dense disablePadding>
            {visible.map((i) => {
              const isDismissed = dismissed.isDismissed(i.id, i.lastEventAt);
              return (
                <ListItemButton
                  key={i.id}
                  onClick={() => {
                    navigate(`/incidents/${i.id}`);
                    setAnchor(null);
                  }}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    py: 1,
                    opacity: isDismissed ? 0.55 : 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {i.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {i.vehicle.name} · {relative(i.lastEventAt)}
                    </Typography>
                  </Box>
                  <RiskBadge level={i.riskLevel} />
                  {!isDismissed && (
                    <Tooltip title="Mark as seen. The incident stays open." describeChild>
                      <IconButton
                        size="small"
                        aria-label={`Mark "${i.title}" as seen`}
                        onClick={(e) => {
                          // Without this the row's navigate fires and the panel
                          // closes on the way to the incident.
                          e.stopPropagation();
                          dismissed.dismiss([{ id: i.id, lastEventAt: i.lastEventAt }]);
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}

        <Divider />
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          {live.length > 0 && (
            <Button
              size="small"
              onClick={() =>
                dismissed.dismiss(
                  live.map((i) => ({ id: i.id, lastEventAt: i.lastEventAt })),
                )
              }
            >
              Mark all as seen
            </Button>
          )}
          {hiddenHere > 0 && (
            <Button size="small" onClick={() => setShowDismissed((v) => !v)}>
              {showDismissed ? 'Hide seen' : `Show ${hiddenHere} seen`}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {dismissed.count > 0 && (
            <Button size="small" color="inherit" onClick={dismissed.restoreAll}>
              Reset
            </Button>
          )}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 2, pb: 1.25 }}
        >
          {/*
            Stated, not implied. Somebody clearing a list of critical incidents
            is entitled to know they have not just closed them.
          */}
          Marking as seen hides an alert here only — the incident stays open, and returns if it
          gets a new event.
        </Typography>
      </Popover>
    </>
  );
}
